<!-- Main Cash Flow Management Application -->
<template>
  <!-- Main container with fixed width and centered layout -->
  <div class="w-[875px] mx-auto mt-3 text-sm">
    <!-- Loading state display -->
    <div v-if="isLoading">Loading...</div>
    <!-- Main application content when data is loaded -->
    <div v-else>
      <!-- Current balance display and editing section -->
      <div class="flex mb-5">
        <div class="label">Balance</div>
        <!-- Inline editing for start balance -->
        <div v-if="isEditing">
          <InputNumber v-model="startBalance" @blur="saveAndExit" :minFractionDigits="2" :maxFractionDigits="2"
            class="w-full border p-2 rounded" />
        </div>
        <!-- Display balance with click-to-edit functionality -->
        <div v-else @click="isEditing = true" class="cursor-pointer">
          {{ format(startBalance, "$0,0.00", { currency: "USD" }) }}
        </div>
        <!--        <div class="text-center w-full"><Button @click="writenew" size="small">Write new DB</Button></div>-->
      </div>
      <!-- Two-column layout for expenses and income -->
      <div class="flex justify-between">
        <!-- Expenses column (left side) -->
        <div class="flex flex-col w-[380px]">
          <!-- Expenses header with add functionality -->
          <div class="header w-full text-center bg-red-600 text-white mb-2"
            @click="popupEdit('E', {} as RecurrentItem, -1)">Expenses
          </div>
          <!-- List of expense items -->
          <div class="cursor-pointer">
            <div v-for="(item, ix) in cashStore.expenses" :key="`${item.description}:${ix}`"
              class="grid grid-cols-[120px_90px_64px_60px] gap-3" @click="popupEdit('E', item, ix)">
              <span>{{ item.description }}</span>
              <span>{{ dayjs.unix(item.nextdue).format("DD MMM YYYY") }}</span>
              <span class="text-right">{{ format(item.amount, "0,0.00") }}</span>
              <span>{{ Frequency[item.frequency] }}</span>
            </div>
          </div>
        </div>
        <!-- Income column (right side) -->
        <div class="flex flex-col w-[380px]">
          <!-- Income header with add functionality -->
          <div class="header w-full text-center bg-green-600 text-white mb-2"
            @click="popupEdit('I', {} as RecurrentItem, -1)">Income
          </div>
          <!-- List of income items -->
          <div class="cursor-pointer">
            <div v-for="(item, ix) in cashStore.income" :key="item.description"
              class="grid grid-cols-[120px_90px_64px_60px] gap-3" @click="popupEdit('I', item, ix)">
              <span>{{ item.description }}</span>
              <span>{{ dayjs.unix(item.nextdue).format("DD MMM YYYY") }}</span>
              <span class="text-right">{{ format(item.amount, "0,0.00") }}</span>
              <span>{{ Frequency[item.frequency] }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
    <!--    <Button type="button" label="Calculate" @click="buildTransactions(startBalance,0,0)"></Button>-->
  </div>

  <!-- Cash flow forecast display section -->
  <div class="w-[875px] mx-auto mt-3 text-sm">
    <!-- Display calculated balances over time -->
    <div class="balances">
      <div v-for="(item) in balances" :key="item.date" class="flex justify-between gap-2">
        <div>{{ dayjs.unix(item.date).format("DD MMM YYYY") }}</div>
        <div class="flex-grow">{{ item.description }}</div>
        <!-- Highlight negative balances in red -->
        <div :class="{ 'text-red-600': item.balance < 0 }">{{ format(item.balance, "0,0.00") }}</div>
      </div>
    </div>
  </div>

  <!-- Modal dialog for adding/editing income and expense items -->
  <!-- Modal dialog for adding/editing income and expense items -->
  <Dialog ref="dialog" v-model:visible="dialogVisible" modal :header="dialogHeader" class="w-[25rem] text-sm">
    <!-- Description input field -->
    <div class="flex items-center gap-4 mb-8">
      <label for="description" class="font-semibold w-24">Description</label>
      <InputText v-model="selectedDescription" id="description" class="flex-auto" />
    </div>
    <!-- Next due date picker -->
    <div class="flex items-center gap-4 mb-8">
      <label for="nextdue" class="font-semibold w-24">Next due</label>
      <DatePicker v-model="selectedNextdue" dateFormat="dd M yy" id="nextdue" class="flex-auto" />
    </div>
    <!-- Amount input with currency formatting -->
    <div class="flex items-center gap-4 mb-8">
      <label for="amount" class="font-semibold w-24">Amount</label>
      <InputNumber v-model="selectedAmount" id="amount" class="flex-auto" mode="currency" currency="USD"
        locale="en-US" />
    </div>
    <!-- Frequency selection dropdown -->
    <div class="flex items-center gap-4 mb-8">
      <label for="frequency" class="font-semibold w-24">Frequency</label>
      <Select v-model="selectedFrequency" id="frequency" class="flex-auto" :options="frequencyItems"
        optionLabel="name" />
    </div>

    <!-- Dialog action buttons -->
    <div class="flex justify-end gap-2">
      <Button type="button" label="Cancel" severity="secondary" @click="dialogVisible = false"></Button>
      <Button type="button" label="Delete" severity="danger" @click="deleteItem"></Button>
      <Button type="button" label="Save" @click="updateItem"></Button>
    </div>
  </Dialog>
</template>

/**
* Main Cash Flow Management Application
*
* This Vue component provides a complete cash flow management interface that allows users to:
* - Manage recurring income and expense items
* - View cash flow forecasts based on recurring transactions
* - Edit account balance and transaction details
* - Visualize future financial position over time
*
* The app integrates with Firebase for real-time data synchronization and uses
* external data sources (like bank account balances) when available.
*/

<script setup lang="ts">
import { computed, onMounted, ref, onBeforeUnmount } from "vue";
import { format } from "numerable";
import dayjs from "dayjs";
import { useCashStore } from "@/stores/cashStore.ts";
import Select from "primevue/select";
import DatePicker from "primevue/datepicker";

import { type BalanceItem, Frequency, type RecurrentItem } from "@/types.ts";
import { buildTransactions } from "@/calc.ts";

// =============================================================================
// REACTIVE STATE VARIABLES
// =============================================================================

/** Current account balance - can be edited inline by clicking */
const startBalance = ref(100);

/** Controls whether the balance field is in edit mode */
const isEditing = ref(false);

/** Controls visibility of the add/edit item dialog */
const dialogVisible = ref(false);

/** Dynamic header text for the dialog based on operation (Add/Edit Income/Expense) */
const dialogHeader = ref("");

/** Available frequency options for recurring items */
const frequencyItems = ref([
  { name: "Once", code: Frequency.Once },
  { name: "Weekly", code: Frequency.Weekly },
  { name: "Fortnightly", code: Frequency.Fortnightly },
  { name: "Monthly", code: Frequency.Monthly },
  { name: "Sixmonthly", code: Frequency.Sixmonthly },
  { name: "Yearly", code: Frequency.Yearly },
]);

// =============================================================================
// DIALOG FORM (edit income/expense) STATE
// =============================================================================

/** Index of the currently selected item for editing (-1 for new items) */
let selectedIx = 0;

/** Type of item being edited: 'E' for Expense, 'I' for Income */
let selectedType: "E" | "I" = "E";

/** Form fields for the dialog */
const selectedDescription = ref("");
const selectedNextdue = ref(new Date());
const selectedAmount = ref(0);
const selectedFrequency = ref({} as { name: string, code: Frequency });

// =============================================================================
// STORE AND COMPUTED STATE
// =============================================================================

/** Pinia store instance for managing cash flow data */
const cashStore = useCashStore();

/** Loading state while initializing data from Firebase */
const isLoading = ref(true);

/** Afterpay and other externally sourced one-time expenses */
const externalExpenses = ref<RecurrentItem[]>([]);

/** Combined list of recurring and external expenses for forecasting */
const allExpenses = computed(() => [...cashStore.expenses, ...externalExpenses.value]);

/** Calculated balance projections over time */
const balances = computed<BalanceItem[]>(() => {
  if (isLoading.value) {
    return [];
  }
  return buildTransactions(startBalance.value, allExpenses.value, cashStore.income);
});

// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================

/** Exit inline editing mode for the balance field */
const saveAndExit = () => {
  isEditing.value = false;
};

// =============================================================================
// DIALOG MANAGEMENT FUNCTIONS
// =============================================================================

/**
 * Open the add/edit dialog for income or expense items
 *
 * @param IorE - Type of item: 'E' for Expense, 'I' for Income
 * @param item - The item to edit (empty object for new items)
 * @param ix - Index of the item (-1 for new items)
 */
function popupEdit(IorE: "E" | "I", item: RecurrentItem, ix: number) {
  // Set dialog header and determine if this is an add or edit operation
  dialogHeader.value = IorE === "E" ? "Edit Expense Item" : "Edit Income Item";

  if (IorE === "E") {
    if (ix < 0) {
      // Adding a new expense
      selectedIx = cashStore.expenses.length;
      dialogHeader.value = "Add Expense Item";
    } else {
      // Editing existing expense
      dialogHeader.value = "Edit Expense Item";
      selectedIx = ix;
    }
  } else {
    if (ix < 0) {
      // Adding a new income item
      selectedIx = cashStore.income.length;
      dialogHeader.value = "Add Income Item";
    } else {
      // Editing existing income item
      dialogHeader.value = "Edit Income Item";
      selectedIx = ix;
    }
  }

  selectedType = IorE;

  // Populate form fields with existing data (or defaults for new items)
  selectedDescription.value = item.description;
  selectedNextdue.value = item.nextdue ? new Date(item.nextdue * 1000) : new Date();
  selectedAmount.value = item.amount;
  selectedFrequency.value = { name: Frequency[item.frequency], code: item.frequency };
  dialogVisible.value = true;
}

/**
 * Delete the currently selected item from the store
 * Removes the item from either expenses or income based on selectedType
 */
function deleteItem() {
  if (selectedType === "E") {
    cashStore.deleteExpense(selectedIx);
  } else {
    cashStore.deleteIncome(selectedIx);
  }

  dialogVisible.value = false;
}

/**
 * Save the current form data as a new or updated item
 * Creates a RecurrentItem object from form data and saves to the appropriate store
 */
function updateItem() {
  // Build item object from form data
  const item = {} as RecurrentItem;
  item.description = selectedDescription.value;
  item.amount = selectedAmount.value;
  item.frequency = selectedFrequency.value.code;
  item.nextdue = selectedNextdue.value.getTime() / 1000; // Convert to Unix timestamp

  // Save to appropriate store based on type
  if (selectedType === "E") {
    cashStore.updateExpense(selectedIx, item);
  } else {
    cashStore.updateIncome(selectedIx, item);
  }

  dialogVisible.value = false;
}

// =============================================================================
// UTILITY FUNCTIONS (CURRENTLY UNUSED)
// =============================================================================

/**
 * Legacy function for database migration
 * Currently commented out in the UI but available for administrative tasks
 */
// @ts-expect-error Do we not use this anymore?
async function writenew() {
  console.log("write new DB");
  await cashStore.writeNewDB();
  console.log("write new DB done");
}

// =============================================================================
// EXTERNAL DATA INTEGRATION
// =============================================================================

/**
 * Global interface for external data collection and Vue ready flag
 * Used to integrate with browser extensions or external scripts that collect
 * bank account balances and transaction data
 */
declare global {
  interface Window {
    collectedData?: { westpac: number, afterpay?: Record<string, number> };
    VUE_READY?: boolean;
  }
}

// =============================================================================
// COMPONENT LIFECYCLE
// =============================================================================

/**
 * Component initialization
 *
 * Handles:
 * - Setting Vue ready flag for external scripts
 * - Store initialization with Firebase data
 * - External data integration via event listener
 * - Error handling for initialization failures
 * - Loading state management
 */
onMounted(async () => {
  console.log("mounted");

  // Set up event listener for external data
  const handleCollectedData = async (event: Event) => {
    const customEvent = event as CustomEvent;
    console.log("Received collectedDataReady event", customEvent.detail);

    try {
      // Initialize the cash store with Firebase data
      await cashStore.initializeStore();

      // Use external balance data if available
      if (customEvent.detail?.westpac) {
        startBalance.value = customEvent.detail.westpac;
        console.log("Set start balance from external data:", customEvent.detail.westpac);
      } else {
        console.error("No westpac data found in collectedDataReady event");
      }
      window.collectedData = {
        afterpay : customEvent.detail?.afterpay ?? {},
        westpac : customEvent.detail?.westpac ?? 0
      };
      // Handle afterpay expenses from external data
      if (customEvent.detail?.afterpay) {
        console.log("Processing afterpay transactions:", customEvent.detail.afterpay);
        window.collectedData.afterpay = customEvent.detail.afterpay;

        // Convert event payload into transient Afterpay expenses
        const afterpayItems : RecurrentItem[] = Object.entries(customEvent.detail.afterpay)
          .reduce((acc : RecurrentItem[], [dateString, amount]) => {
            const parsed = dayjs(dateString);
            if (!parsed.isValid()) {
              console.warn("Skipping invalid afterpay date", dateString);
              return acc;
            }
            const expense = {
              description : "Afterpay",
              amount      : Number(amount),
              frequency   : Frequency.Once,
              nextdue     : parsed.unix(),
            } as RecurrentItem;
            console.log("Prepared afterpay expense:", expense);
            acc.push(expense);
            return acc;
          }, []);

        externalExpenses.value = afterpayItems;
      } else {
        console.error("No afterpay data found in collectedDataReady event");
        externalExpenses.value = [];
      }

      // Debug logging for store state
      console.log("cashStore.expenses: ", cashStore.expenses);
      console.log("cashStore.income: ", cashStore.income);

      isLoading.value = false;
      console.log("Initialization complete");
    } catch (error) {
      console.error("Failed to initialize store:", error);
      isLoading.value = false; // Set to false even on error to show the UI
    }
  };

  // Add event listener for collected data
  window.addEventListener('collectedDataReady', handleCollectedData);

  // Set Vue ready flag for external scripts to detect
  window.VUE_READY = true;
  console.log("Vue ready flag set");

  // Cleanup event listener when component unmounts
  onBeforeUnmount(() => {
    window.removeEventListener('collectedDataReady', handleCollectedData);
  });

  console.log("Event listener set up, waiting for collectedDataReady event");
  console.log("mounted exit");
});
</script>

<!-- =============================================================================
COMPONENT STYLES
============================================================================= -->
<style lang="scss">
/* Bold labels with consistent spacing */
.label {
  font-weight: bold;
  margin-right: 1rem;
}

/* Interactive headers that show add functionality on hover */
.header {
  &:hover {
    cursor: pointer;

    /* Show "+" indicator when hovering over headers */
    &::after {
      content: " +";
      display: inline-block;
    }
  }
}
</style>
